/* global APP_ENV, mixpanel */

/*
 * Env-aware tracking initialization utils
 *
 * Inject APP_ENV via global var or build tools (Webpack DefinePlugin)
 *
 * Recommended usage: create a separate module that initializes
 * the libs and exports the instances:
 *
 * export const mixpanel = initMixpanel('MY MIXPANEL PUBLISHABLE TOKEN');
 * export const rollbar = initRollbar('MY ROLLBAR PUBLISHABLE TOKEN');
 *
 */

import Rollbar from 'rollbar';

import gdpr from '../util/gdpr';

const appEnv = typeof APP_ENV !== `undefined` ? APP_ENV : `development`;
const isBrowser = typeof window !== `undefined`;

export const commonTrackingEventPropertyName = {
  reportName: `Report name`,
  dataGroupId: `Data Group Id`,
  segmentByGroupProperty: `Segment by a group property`,
  filterByGroupProperty: `Filter by a group property`,
};

function isMixpanelInitialized({instanceName = `mixpanel`, jsLib = null} = {}) {
  jsLib = jsLib || mixpanel;
  const jsLibInstance = instanceName && instanceName !== `mixpanel` ? jsLib[instanceName] : jsLib;
  return !!(jsLibInstance && jsLibInstance.config);
}

export function initMixpanel(token, instanceName, jsLibOpts = {}, jsLib = null) {
  jsLib = jsLib || mixpanel;
  if (isMixpanelInitialized({instanceName, jsLib})) {
    return jsLib;
  }

  const config = {
    persistence: `localStorage`,
  };

  if (appEnv !== `production`) {
    token = `9c4e9a6caf9f429a7e3821141fc769b7`; // Project 132990 Mixpanel Dev

    if (isBrowser) {
      // Use local api when running on a devbox
      // Note: we cannot rely on APP_ENV since a js app can be running on any host
      // (e.g. stand-alone mode)
      const host = window.location.host;
      const isDev = /([a-zA-Z0-9]+).dev.mixpanel.org/.test(host);
      if (isDev) {
        config[`api_host`] = `${window.location.protocol}//${host}`;
      }

      // Use local app or staging app when running on devbox or staging server
      const isStage = /stage([0-9]+).mixpanel.com/.test(host);
      if (isDev || isStage) {
        config[`app_host`] = `${window.location.protocol}//${host}`;
      }
    } else {
      config[`api_host`] = `https://test.com`;
    }
  }

  // GDPR-restrict tracking until it is allowed
  if (!gdpr.allow_tracking) {
    config[`opt_out_tracking_by_default`] = true;
  }

  jsLib.init(token, Object.assign(config, jsLibOpts), instanceName);

  return jsLib;
}

export function initRollbar(token) {
  // Rollbar overrides console.* APIs and 'error'/'unhandledrejection' events. Disable it in dev mode,
  // otherwise console sources always point to rollbar other than script invoking console API. Also needs
  // to be disabled for mocha unit tests since these overrides are causing some errors to be swallowed.
  const isDev = appEnv === `development`;

  return new Rollbar({
    enabled: gdpr.allow_snippets, // GDPR-restrict rollbar
    accessToken: token,
    captureIp: `anonymize`, // semi-anonymization on the captured IP address by masking out the least significant bits
    captureUncaught: !isDev,
    captureUnhandledRejections: !isDev,
    autoInstrument: !isDev,
    payload: {
      autoInstrument: {
        dom: isBrowser,
        navigation: isBrowser,
        network: false,
      },
      environment: appEnv,
      client: {
        javascript: {
          // Rollbar will not source map errors without a code_version
          // We use asset-cache/<hash>/app.bundle.min.js so new versions will always change paths
          code_version: `1`, // eslint-disable-line camelcase
        },
      },
    },
  });
}

export function getNavTiming() {
  if (!isBrowser) {
    return null;
  }

  // Assumes we have access to window.performance.timing
  const pt = window.performance.timing;

  // All times are recorded relative to navigationStart
  const start = pt.navigationStart;

  // Capture project_id from pathname if possible
  let projectId = null;
  const projectIdPattern = /report\/(\d+)/;
  const projectIdMatch = projectIdPattern.exec(window.location.pathname);
  if (projectIdMatch && projectIdMatch.length > 1) {
    projectId = projectIdMatch[1];
  }

  const timing = {
    hash: window.location.hash,
    // This holds window.performance.timing.<metric> - start
    navigation_metrics: {},
    pageload_id: start + window.location.pathname,
    path: window.location.pathname + window.location.hash,
    pathname: window.location.pathname,
    project_id: projectId,
    distinct_id: projectId,
  };

  // Record timedeltas
  // Reference: https://hpbn.co/primer-on-web-performance/#synthetic-and-real-user-performance-measurement
  const deltas = {
    redirect_ms: pt.redirectEnd - pt.redirectStart,
    dns_lookup_ms: pt.domainLookupEnd - pt.domainLookupStart,
    tcp_setup_ms: pt.connectEnd - pt.connectStart,
    ttfb_ms: pt.responseStart - pt.requestStart,
    transmission_ms: pt.responseEnd - pt.responseStart,
    dom_html_load_ms: pt.domContentLoadedEventStart - pt.domLoading,
    dom_complete_load_ms: pt.domComplete - pt.domContentLoadedEventStart,
    load_event_ms: pt.loadEventEnd - pt.domComplete,
    page_load_ms: pt.loadEventEnd - pt.navigationStart,
  };
  for (const k in deltas) {
    if (deltas.hasOwnProperty(k) && !isNaN(deltas[k])) {
      timing[k] = deltas[k];
    }
  }

  // Track the rest of the NavigationAPI timings relative to navigationStart
  for (const key in pt) {
    if (typeof pt[key] !== `function`) {
      timing.navigation_metrics[key] = pt[key] - start;
    }
  }

  return {timing, start};
}
